import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatTime, getOrdinal } from '../../utils/formatters';
import { containerVariants, itemVariants } from '../../utils/animations';
import LeagueBadge from '../league/LeagueBadge';

// Letter-by-letter animation for "Puzzle Complete!" header
const letterVariants = {
  hidden: {
    opacity: 0,
    y: -20,
    scale: 0,
    rotate: -5,
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 15,
      delay: i * 0.04,
    },
  }),
};

const headerContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.04,
    },
  },
};

// Pre-split text for animation (avoids array index key warning)
const PUZZLE_COMPLETE_LETTERS = 'Puzzle Complete!'.split('').map((char, i) => ({
  id: `pc-${i}`,
  char,
  index: i,
}));

function ResultsModal({
  elapsedTime,
  moveCount,
  pieceCount,
  puzzleNumber,
  puzzleTitle,
  puzzleDescription,
  onClose,
  currentStreak,
  longestStreak,
  rating,
  onRatingSelect,
  newBadges,
  isGuest,
  // League context props
  leaguePoints,
  leagueTier,
  // Room context props
  roomCode,
  rank,
  totalParticipants,
}) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const RATING_OPTIONS = [
    { value: 'happy', emoji: '😊', label: 'Loved it' },
    { value: 'neutral', emoji: '😐', label: 'It was okay' },
    { value: 'sad', emoji: '😞', label: 'Not great' },
  ];

  // Handle backdrop click to close
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Navigate to room leaderboard
  const handleViewLeaderboard = () => {
    navigate(`/room/${roomCode}`);
  };

  const handleShare = async () => {
    let shareText;

    if (roomCode) {
      // Room puzzle share format
      shareText = `Puzzlists 🧩
🏆 ${getOrdinal(rank)} of ${totalParticipants} players
⏱️ ${formatTime(elapsedTime)} • 🎯 ${moveCount} moves • 🧩 ${pieceCount} pieces`;
    } else {
      // Solo puzzle share format
      const streakLine = currentStreak >= 3 ? `\n🔥 ${currentStreak} day streak` : '';
      shareText = `Puzzlists #${puzzleNumber} 🧩
⏱️ ${formatTime(elapsedTime)} • 🎯 ${moveCount} moves • 🧩 ${pieceCount} pieces${streakLine}`;
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Puzzlists Results',
          text: shareText,
        });
      } else {
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      // User cancelled share or clipboard failed
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      role="presentation"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.15 }}
    >
      <motion.div
        className="bg-puzzle-surface rounded-2xl p-6 max-w-sm w-full shadow-xl border border-puzzle-border relative"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', duration: 0.4, bounce: 0.25 }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          type="button"
          className="absolute top-4 right-4 p-1 text-puzzle-text-secondary hover:text-puzzle-text transition-colors"
          aria-label="Close results"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header with letter-by-letter animation */}
        <motion.div
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ delay: 0.8, duration: 0.4, ease: 'easeInOut' }}
        >
          <motion.h2
            className="text-2xl font-bold text-center text-puzzle-green mb-6 flex justify-center"
            variants={headerContainerVariants}
            initial="hidden"
            animate="visible"
          >
            {PUZZLE_COMPLETE_LETTERS.map((letter) => (
              <motion.span
                key={letter.id}
                variants={letterVariants}
                custom={letter.index}
                className={letter.char === ' ' ? 'w-2' : ''}
              >
                {letter.char === ' ' ? '\u00A0' : letter.char}
              </motion.span>
            ))}
          </motion.h2>
        </motion.div>

        {/* Puzzle Title & Description */}
        {puzzleTitle && (
          <motion.div
            className="text-center mb-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-lg font-semibold text-puzzle-text">
              {puzzleTitle}
            </h3>
            {puzzleDescription && (
              <p className="text-sm text-puzzle-text-secondary mt-1">
                {puzzleDescription}
              </p>
            )}
          </motion.div>
        )}

        {/* Stats */}
        <motion.div
          className="space-y-3 mb-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="flex items-center justify-between py-2 border-b border-puzzle-border"
            variants={itemVariants}
          >
            <span className="text-puzzle-blue flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Time
            </span>
            <span className="font-semibold text-puzzle-text">{formatTime(elapsedTime)}</span>
          </motion.div>
          <motion.div
            className="flex items-center justify-between py-2 border-b border-puzzle-border"
            variants={itemVariants}
          >
            <span className="text-puzzle-yellow flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              Moves
            </span>
            <span className="font-semibold text-puzzle-text">{moveCount}</span>
          </motion.div>
          <motion.div
            className="flex items-center justify-between py-2 border-b border-puzzle-border"
            variants={itemVariants}
          >
            <span className="text-puzzle-red flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              Pieces
            </span>
            <span className="font-semibold text-puzzle-text">{pieceCount}</span>
          </motion.div>
          {/* Streak Section */}
          {currentStreak !== undefined && (
            <>
              <motion.div
                className="flex items-center justify-between py-2 border-b border-puzzle-border"
                variants={itemVariants}
              >
                <span className="text-orange-500 flex items-center gap-2">
                  <span className="text-lg">🔥</span>
                  Current Streak
                </span>
                <span className="font-semibold text-puzzle-text">
                  {currentStreak}
                  {' '}
                  {currentStreak === 1 ? 'day' : 'days'}
                </span>
              </motion.div>
              <motion.div
                className="flex items-center justify-between py-2"
                variants={itemVariants}
              >
                <span className="text-yellow-500 flex items-center gap-2">
                  <span className="text-lg">🏆</span>
                  Best Streak
                </span>
                <span className="font-semibold text-puzzle-text">
                  {longestStreak}
                  {' '}
                  {longestStreak === 1 ? 'day' : 'days'}
                </span>
              </motion.div>
            </>
          )}
        </motion.div>

        {/* League Points Section */}
        {leaguePoints > 0 && leagueTier && (
          <motion.div
            className="mb-6 pt-4 border-t border-puzzle-border"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <div className="flex items-center justify-center gap-3">
              <LeagueBadge tier={leagueTier} size="sm" />
              <span className="text-lg font-bold text-puzzle-text">
                +
                {leaguePoints}
                {' '}
                rating pts
              </span>
            </div>
            <p className="text-xs text-puzzle-text-secondary text-center mt-1">
              <Link to="/league" className="text-puzzle-blue hover:underline">
                View your league
              </Link>
            </p>
          </motion.div>
        )}

        {/* New Badges Section */}
        {newBadges && newBadges.length > 0 && (
          <motion.div
            className="mb-6 pt-4 border-t border-puzzle-border"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-sm text-puzzle-text-secondary text-center mb-3">
              {newBadges.length === 1 ? 'Badge Unlocked!' : 'Badges Unlocked!'}
            </p>
            <div className="flex justify-center gap-4">
              {newBadges.map((badge) => (
                <motion.div
                  key={badge.id}
                  className="bg-puzzle-background rounded-xl p-3 text-center ring-2 ring-puzzle-blue"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                    delay: 0.6,
                  }}
                >
                  <div className="text-3xl mb-1">{badge.icon}</div>
                  <div className="text-sm font-medium text-puzzle-text">{badge.name}</div>
                  <div className="text-xs text-puzzle-text-secondary">{badge.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Rating Section */}
        {onRatingSelect && (
          <motion.div
            className="text-center mb-6 pt-4 border-t border-puzzle-border"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-sm text-puzzle-text-secondary mb-3">Rate this puzzle</p>
            <div className="flex justify-center gap-4">
              {RATING_OPTIONS.map((option) => (
                <motion.button
                  key={option.value}
                  type="button"
                  onClick={() => onRatingSelect(option.value)}
                  title={option.label}
                  className={`text-3xl p-2 rounded-lg transition-all ${
                    rating === option.value
                      ? 'bg-puzzle-blue/20 scale-110 ring-2 ring-puzzle-blue'
                      : 'hover:bg-puzzle-hover hover:scale-105'
                  }`}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {option.emoji}
                </motion.button>
              ))}
            </div>
            {rating && (
              <motion.p
                className="text-xs text-green-600 mt-2"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
              >
                Thanks for your feedback!
              </motion.p>
            )}
          </motion.div>
        )}

        {/* Guest Sign-Up Prompt */}
        {isGuest && (
          <motion.div
            className="mb-6 pt-4 border-t border-puzzle-border text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <p className="text-sm text-puzzle-text-secondary mb-3">
              Create a free account to save your stats and track your streaks!
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 py-2 px-5 bg-puzzle-blue text-white text-sm font-medium rounded-full hover:bg-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Sign Up
            </Link>
            <p className="text-xs text-puzzle-text-secondary mt-2">
              Already a user?
              {' '}
              <Link to="/login" className="font-semibold text-puzzle-blue hover:text-blue-600">
                Login
              </Link>
            </p>
          </motion.div>
        )}

        {/* Buttons */}
        <motion.div
          className={`flex gap-3 ${roomCode ? 'flex-col' : ''}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {/* View Leaderboard - only for room puzzles */}
          {roomCode && (
            <motion.button
              onClick={handleViewLeaderboard}
              type="button"
              className="w-full py-3 px-4 bg-puzzle-blue text-white font-medium rounded-full hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              View Leaderboard
            </motion.button>
          )}
          <div className="flex gap-3">
            <motion.button
              onClick={handleShare}
              type="button"
              className="flex-1 py-3 px-4 bg-puzzle-green text-white font-medium rounded-full hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {copied ? (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </>
              )}
            </motion.button>
            <motion.button
              onClick={onClose}
              type="button"
              className="flex-1 py-3 px-4 border-2 border-puzzle-border text-puzzle-text font-medium rounded-full hover:border-puzzle-text-secondary transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

ResultsModal.propTypes = {
  elapsedTime: PropTypes.number.isRequired,
  moveCount: PropTypes.number.isRequired,
  pieceCount: PropTypes.number.isRequired,
  puzzleNumber: PropTypes.number.isRequired,
  puzzleTitle: PropTypes.string,
  puzzleDescription: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  currentStreak: PropTypes.number,
  longestStreak: PropTypes.number,
  rating: PropTypes.oneOf(['happy', 'neutral', 'sad', null]),
  onRatingSelect: PropTypes.func,
  newBadges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      icon: PropTypes.string,
    }),
  ),
  isGuest: PropTypes.bool,
  // League context props
  leaguePoints: PropTypes.number,
  leagueTier: PropTypes.string,
  // Room context props
  roomCode: PropTypes.string,
  rank: PropTypes.number,
  totalParticipants: PropTypes.number,
};

ResultsModal.defaultProps = {
  puzzleTitle: null,
  puzzleDescription: null,
  currentStreak: undefined,
  longestStreak: undefined,
  rating: null,
  onRatingSelect: null,
  newBadges: [],
  isGuest: false,
  leaguePoints: null,
  leagueTier: null,
  roomCode: null,
  rank: null,
  totalParticipants: null,
};

export default ResultsModal;
